import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  Signal,
  ViewChild
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { Routing } from '../../app.routes';
import { PoliciesMode } from '../../shared/helpers/client.utils';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { StepperService } from '../../shared/services/stepper.service';
import { AuthStateService } from '../../shared/services/state/auth-state.service';
import { ProjectStateService } from '../../shared/services/state/project-state.service';

@Component({
  selector: 'app-acs-stepper',
  imports: [
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    TranslatePipe
  ],
  templateUrl: './stepper.component.html',
  styles: `
    @media only screen and (min-width: 1224px) {
      .margin-desktop {
        padding-inline: var(--xl-stepper-padding-inline);
      }

      ::ng-deep
      .mat-stepper-label-position-bottom
      .mat-horizontal-stepper-header
      .mat-step-icon {
        height: 40px;
        width: 40px;
        top: -8px;
      }

      ::ng-deep .mat-stepper-horizontal-line.mat-stepper-horizontal-line {
        border-top-style: dashed;
        border-top-width: 2px;
      }

      ::ng-deep
      .mat-stepper-label-position-bottom
      .mat-horizontal-stepper-header:not(:last-child)::after,
      ::ng-deep
      .mat-stepper-label-position-bottom
      .mat-horizontal-stepper-header:not(:last-child)::before,
      ::ng-deep
      .mat-stepper-label-position-bottom
      .mat-horizontal-stepper-header:not(:first-child)::before {
        border-top-style: dashed !important;
        border-top-width: 2px !important;
      }
    }

    /* Masque l'espace du contenu de chaque step, non utilisé */
    ::ng-deep .mat-horizontal-content-container {
      display: none !important;
    }

    /* Masque la ligne native du stepper */
    :host ::ng-deep .mat-stepper-horizontal-line,
    :host ::ng-deep .mat-horizontal-stepper-header::before,
    :host ::ng-deep .mat-horizontal-stepper-header::after {
      display: none !important;
      content: none !important;
      border: 0 !important;
      width: 0 !important;
      height: 0 !important;
    }

    ::ng-deep
    .mat-stepper-label-position-bottom
    .mat-horizontal-stepper-header-container {
      justify-content: space-between;
    }

    :host ::ng-deep .mat-horizontal-stepper-header .mat-step-icon {
      position: relative;
      z-index: 2;
    }

    /*
      Fil d'ariane verrouillé (client reprenant un projet apporteur) : les clics sont neutralisés
      et les steps franchis conservent le visuel "validé" de l'état "edit".
    */
    :host ::ng-deep .stepper-locked {
      --mat-stepper-header-done-state-icon-background-color: #cbf8b4;
      --mat-stepper-header-done-state-icon-foreground-color: #136d1f;
    }

    :host ::ng-deep .stepper-locked .mat-horizontal-stepper-header {
      pointer-events: none;
      cursor: default;
    }

    ::ng-deep :root {
      /* Taille adaptative : 1rem dès qu'il y a la place, réduite sur petits
         écrans pour que les titres tiennent sur une seule ligne */
      --mat-stepper-header-selected-state-label-text-size: clamp(
        0.75rem,
        3.6vw,
        1rem
      );
      --mat-stepper-header-label-text-size: clamp(0.75rem, 3.6vw, 1rem);
      --mat-stepper-header-selected-state-icon-background-color: #3bafdd;
      --mat-stepper-header-edit-state-icon-background-color: #cbf8b4;
      --mat-stepper-header-edit-state-icon-foreground-color: #136d1f;
      --mat-stepper-line-color: #3bafdd;
      --mat-stepper-header-icon-background-color: #dce2e2;
      --mat-stepper-header-icon-foreground-color: #a7b2c5;
      --mat-stepper-header-done-state-icon-background-color: #a7b2c5;
      --mat-stepper-container-color: none;
    }

    ::ng-deep .mat-step-icon .mat-icon {
      font-weight: 700;
    }

    :host ::ng-deep .mat-stepper-label-position-bottom .mat-step-text-label {
      white-space: nowrap;
      overflow-wrap: normal;
      text-overflow: ellipsis;
      text-align: center;
      line-height: 1.2;
    }

    @media only screen and (max-width: 1223px) {
      :host
        ::ng-deep
        .mat-stepper-label-position-bottom
        .mat-horizontal-stepper-header {
        padding-inline: 0.25rem;
      }

      :host
        ::ng-deep
        .mat-stepper-label-position-bottom
        .mat-horizontal-stepper-header
        .mat-step-label {
        padding-top: 0.75rem;
      }

      :host
        ::ng-deep
        .mat-stepper-label-position-bottom
        .mat-horizontal-stepper-header-container {
        padding-inline: 0.5rem;
      }
    }

    .rail {
      position: absolute;
      width: 100%;
      left: 0;
      right: 0;
      top: 2rem;
      height: 64px;
      transform: translateY(-50%);
      pointer-events: none;
      z-index: 1;
    }
  `,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StepperComponent implements AfterViewInit, OnDestroy {
  @ViewChild('stepper') private readonly stepper: MatStepper | undefined;
  @ViewChild('rail') private rail!: ElementRef<SVGSVGElement>;
  @ViewChild('railPath') private railPath!: ElementRef<SVGPathElement>;

  readonly #stepperService: StepperService = inject(StepperService);
  readonly #router: Router = inject(Router);
  readonly authStateService: AuthStateService = inject(AuthStateService);
  readonly projectStateService: ProjectStateService =
    inject(ProjectStateService);
  private host = inject(ElementRef<HTMLElement>);
  private ro?: ResizeObserver;
  #rafId?: number;

  #isAfterViewInit: boolean = false;

  public validProject = new FormControl('');
  public validInfo = new FormControl('');
  public validTarif = new FormControl('');
  public validSouscription = new FormControl('');

  private updateRailOffsets = () => {
    if (!this.rail || !this.railPath) return;

    const icons = Array.from(
      this.host.nativeElement.querySelectorAll(
        '.mat-horizontal-stepper-header .mat-step-icon'
      ) as NodeListOf<HTMLElement>
    );
    if (icons.length < 2) return;

    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const centers = icons.map((icon) => {
      const rect = icon.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2 - hostRect.left,
        y: rect.top + rect.height / 2 - hostRect.top
      };
    });

    const first = centers[0];
    const last = centers[centers.length - 1];
    const span = last.x - first.x;
    if (span <= 0) return;

    // Le viewBox horizontal (0 -> 100) est cale entre le premier et le dernier cercle.
    const rail = this.rail.nativeElement;
    rail.style.paddingLeft = `${first.x}px`;
    rail.style.paddingRight = `${hostRect.width - last.x}px`;
    // Le trace est centre verticalement sur y = 32 dans un viewBox de 64 de haut,
    // et .rail applique translateY(-50%) : `top` doit donc valoir la hauteur du centre.
    rail.style.top = `${first.y}px`;

    // Vague alternee : bosse vers le haut, puis vers le bas, etc. Les points de controle
    // sont poses au tiers et aux deux tiers de chaque segment pour rester symetriques.
    const toViewBox = (x: number) => ((x - first.x) / span) * 100;
    let d = 'M 0 32';
    for (let i = 1; i < centers.length; i++) {
      const from = toViewBox(centers[i - 1].x);
      const to = toViewBox(centers[i].x);
      const width = to - from;
      const controlY = i % 2 === 1 ? 16 : 48;
      d +=
        ` C ${(from + width / 3).toFixed(3)} ${controlY},` +
        ` ${(to - width / 3).toFixed(3)} ${controlY},` +
        ` ${to.toFixed(3)} 32`;
    }
    this.railPath.nativeElement.setAttribute('d', d);
  };

  public stepperIndex: Signal<number> = computed(() => {
    return this.#stepperService.step();
  });

  /**
   * Client reprenant un projet créé lors d'un parcours apporteur (MC-6784) : le fil d'ariane
   * reste affiché pour situer le client dans le parcours, mais il est verrouillé (clics
   * neutralisés, aucun retour en arrière possible). Ne s'applique ni à l'apporteur, ni à un
   * client venu du parcours grand public (comportement actuel conservé dans ces deux cas).
   */
  public isLockedForClient: Signal<boolean> =
    this.authStateService.selectIsClientOfProducerProject();

  // Effect pour gérer les effets de bord liés aux changements de step
  private readonly stepEffect = effect(() => {
    const step = this.stepperIndex();
    if (this.stepper) {
      this.stepper.selectedIndex = step;
      this.setStepValidation(step);
      //Ici on sait qu'on a passer l'initilisation de tout le composant
      this.#isAfterViewInit = true;
      // Les libelles et les icones changent d'une etape a l'autre : on reajuste le fil.
      this.updateRailOffsets();
    }
  });

  /**
   * Suivi des etapes de velidation du stepper
   * On init a 0 tout les formControl a required: true
   * sur chaque etape on envele cette contrainte et on fait avencer le stepper
   * @param step
   * @private
   */
  private setStepValidation(step: number): void {
    if (step === 0) {
      this.validProject.setErrors({ required: true }, { emitEvent: false });
      this.validInfo.setErrors({ required: true }, { emitEvent: false });
      this.validTarif.setErrors({ required: true }, { emitEvent: false });
      this.validTarif.setErrors({ required: true }, { emitEvent: false });
    }
    if (step >= 1) {
      this.validProject.reset();
      this.stepper?.next();
    }
    if (step >= 2) {
      this.validInfo.reset();
      this.stepper?.next();
    }
    if (step >= 3) {
      this.validTarif.reset();
      this.stepper?.next();
    }
  }

  /**
   * On attend que le composant soit bien init pour passer dans le router
   * Sur chaque step de clique, on repasse le signal a 0 pour qu'à l'init du composant ça soit la bonne step qui soit choisi,
   * on Remet le formControl du step qu'on quitte à required: true pour empecher la navigation sur ce composant
   * On met a jour le path du guard à chaque chamgenement de step
   * @param index number numero du step selectionnée
   */
  public onChangeIndex(index: number): void {
    this.#stepperService.step.set(index);
    if (this.isLockedForClient()) {
      // Fil d'ariane verrouillé : aucun changement d'étape ne doit déclencher de navigation.
      return;
    }
    const param = this.authStateService.getSessionStorageUrlParamInfo();
    if (this.#isAfterViewInit) {
      switch (index) {
        case 0:
          this.stepper!.selectedIndex = index;
          this.projectStateService.setGuardPath('');
          if (param?.entity === PoliciesMode.CLIENTS) {
            this.#router
              .navigate([Routing.PROJECT], {
                queryParams: {
                  token:
                    this.authStateService.getVisitorParam()?.visitor_token ??
                    param.token,
                  uuid: param?.uuid,
                  producer_code:
                    this.authStateService.getVisitorParam()?.broker_id,
                  mode: param?.mode,
                  entity: param?.entity
                },
                replaceUrl: true
              })
              .then();
          } else {
            this.#router
              .navigate([Routing.PROJECT], {
                queryParams: {
                  token: param?.token,
                  uuid: param?.uuid,
                  producer_code: param?.producerCode,
                  mode: param?.mode,
                  entity: param?.entity
                },
                replaceUrl: true
              })
              .then();
          }
          break;
        case 1:
          this.stepper!.selectedIndex = index;
          this.projectStateService.setGuardPath(Routing.INFO);
          this.#router.navigateByUrl(Routing.INFO, { replaceUrl: true }).then();
          this.validInfo.setErrors({ required: true });
          break;
        case 2:
          this.stepper!.selectedIndex = index;
          this.projectStateService.setGuardPath(Routing.TARIFS);
          this.#router
            .navigateByUrl(Routing.TARIFS, { replaceUrl: true })
            .then();
          this.validTarif.setErrors({ required: true });
          break;
      }
    }
  }

  /**
   * Le Mat stepper est init seulement apres ce hook donc initialisation quand on a le mat stepper
   */
  public ngAfterViewInit(): void {
    this.setStepValidation(this.stepperIndex());
    this.updateRailOffsets();
    this.ro = new ResizeObserver(this.updateRailOffsets);
    const container = this.host.nativeElement.querySelector(
      '.mat-horizontal-stepper-header-container'
    ) as HTMLElement | null;
    if (container) this.ro.observe(container);
    // On observe aussi l'hote : si le container n'existe pas encore, on recuperera
    // quand meme une mesure valide des que la mise en page se stabilise.
    this.ro.observe(this.host.nativeElement);
    this.#rafId = requestAnimationFrame(() => this.updateRailOffsets());
    window.addEventListener('resize', this.updateRailOffsets);
  }

  ngOnDestroy(): void {
    if (this.#rafId !== undefined) cancelAnimationFrame(this.#rafId);
    this.ro?.disconnect();
    window.removeEventListener('resize', this.updateRailOffsets);
  }
}
